import { HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, Inject, Injector, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, of, tap, throwError as observableThrowError, catchError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { LoaderServices } from '../services/loader.service';
import { environment } from 'src/environments/environment';

interface ServerGetCacheEntry {
   expiresAt: number;
   response: HttpResponse<any>;
}

const SERVER_GET_CACHE_TTL_MS = 5 * 60 * 1000;
const SERVER_GET_CACHE_MAX_ENTRIES = 500;
const serverGetCache = new Map<string, ServerGetCacheEntry>();

@Injectable()
export class HeaderInterceptor implements HttpInterceptor {
   private isBrowser: boolean;
   private appHeaderHosts = this.getAppHeaderHosts();
   private publicCacheHosts = this.getPublicCacheHosts();

   constructor(
      private authService: AuthService,
      private loader: LoaderServices,
      private injector: Injector,
      @Inject(PLATFORM_ID) private platformId: Object
   ) {
      this.isBrowser = isPlatformBrowser(this.platformId);
   }

   private getAppHeaderHosts(): Set<string> {
      const endpointUrls = [
         environment.apiUrl,
         environment.tradingPlatformUrl,
      ].filter(Boolean);

      return new Set(
         endpointUrls
            .map((url) => this.getHost(url))
            .filter((host): host is string => Boolean(host))
      );
   }

   private getHost(url: string): string | null {
      try {
         return new URL(url).host;
      } catch {
         return null;
      }
   }

   private shouldAddAppHeaders(url: string): boolean {
      const host = this.getHost(url);

      return Boolean(host && this.appHeaderHosts.has(host));
   }

   private getPublicCacheHosts(): Set<string> {
      const urls = [
         environment.typeSense.url,
         environment.chartCollection.url,
         environment.nove_infomavan_v2.url,
         environment.statementCollection.url,
         environment.new_statement_data.url,
         environment.ipo_data.url,
         environment.uatTypeSense.url,
      ].filter(Boolean);

      return new Set(
         urls
            .map((url) => this.getHost(url))
            .filter((host): host is string => Boolean(host))
      );
   }

   private isNewFinancialApiRequest(url: string): boolean {
      const newFinancialApiUrl = environment.nova_data_api.url.replace(/\/$/, '');

      return url.includes(newFinancialApiUrl);
   }

   private isMultipartRequest(req: HttpRequest<any>): boolean {
      return typeof FormData !== 'undefined' && req.body instanceof FormData;
   }

   private hasRealAuthorization(headers: HttpHeaders): boolean {
      const authorization = headers.get('Authorization')?.trim();
      return Boolean(
         authorization &&
         !['Bearer', 'Bearer null', 'Bearer undefined'].includes(authorization)
      );
   }

   private shouldUseServerGetCache(req: HttpRequest<any>): boolean {
      if (this.isBrowser || req.method !== 'GET') {
         return false;
      }

      if (this.hasRealAuthorization(req.headers)) {
         return false;
      }

      const host = this.getHost(req.url);
      if (!host || !this.publicCacheHosts.has(host)) {
         return false;
      }

      return !/(\/user\/|\/auth\/|portfolio|watchlist|subscription|stripe|payment|trade|login|logout|refresh)/i.test(req.urlWithParams);
   }

   private getServerCacheKey(req: HttpRequest<any>): string {
      return `${req.method}:${req.urlWithParams}`;
   }

   private getCachedServerResponse(req: HttpRequest<any>): HttpResponse<any> | null {
      const cacheKey = this.getServerCacheKey(req);
      const cached = serverGetCache.get(cacheKey);

      if (!cached) {
         return null;
      }

      if (cached.expiresAt <= Date.now()) {
         serverGetCache.delete(cacheKey);
         return null;
      }

      return cached.response.clone();
   }

   private cacheServerResponse(req: HttpRequest<any>, response: HttpResponse<any>): void {
      if (serverGetCache.size >= SERVER_GET_CACHE_MAX_ENTRIES) {
         const firstKey = serverGetCache.keys().next().value;
         if (firstKey) {
            serverGetCache.delete(firstKey);
         }
      }

      serverGetCache.set(this.getServerCacheKey(req), {
         expiresAt: Date.now() + SERVER_GET_CACHE_TTL_MS,
         response: response.clone(),
      });
   }

   private clearSubscriptionInfo(): void {
      void import('../services/subscription.service')
         .then(({ SubscriptionService }) => {
            this.injector.get(SubscriptionService).clearSubscriptionInfo();
         })
         .catch(() => {});
   }

   private handleForbiddenError(err: any): void {
      void import('../services/shared.service')
         .then(({ SharedService }) => {
            const sharedService = this.injector.get(SharedService);
            sharedService.closeAllPopup();

            switch (err.error?.status_code) {
               case 'LIMIT_REACHED':
                  sharedService.openLimitReachedPopup(
                     err.error.title,
                     err.error.message,
                     err.error.can_upgrade
                  );
                  break;
               case 'UNAUTHORIZED':
                  sharedService.openUpgradePopup();
                  break;
            }
         })
         .catch(() => {});
   }


   intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
      // if (!isPlatformBrowser(this.platformId)) return next.handle(req);
      let token = this.authService.getToken();
      // let baseHref = location.pathname.split('/').splice(1, 1)[0];
      // if (baseHref !== 'ru' && baseHref !== 'en' && baseHref !== 'uz') {
      //    baseHref = 'ru';
      // }

      const timezoneOffset = -1*((new Date()).getTimezoneOffset())
      const isNewFinancialApiRequest = this.isNewFinancialApiRequest(req.url);
      let header = req.headers
         .set('Content-Type', 'application/json')
         .set('Accept', 'application/json');

      if (!isNewFinancialApiRequest) {
         header = header.set('User-Timezone-Offset', timezoneOffset.toString());
      }

      if (this.shouldAddAppHeaders(req.url)) {
         header = header
            .set('device-type', 'web')
            .set('version-code', environment.buildnumber.toString());
      }

      const typeSenseConfigs = [
         { url: environment.typeSense.url, key: environment.typeSense.searchApiKey },
         { url: environment.chartCollection.url, key: environment.chartCollection.searchApiKey },
         { url: environment.nove_infomavan_v2.url, key: environment.nove_infomavan_v2.searchApiKey },
         // { url: environment.typeSenseFinancial.url, key: environment.typeSenseFinancial.searchApiKey },
         { url: environment.statementCollection.url, key: environment.statementCollection.searchApiKey },
         { url: environment.new_statement_data.url, key: environment.new_statement_data.searchApiKey },
         { url: environment.ipo_data.url, key: environment.ipo_data.searchApiKey },
         { url: environment.uatTypeSense.url, key: environment.uatTypeSense.searchApiKey }
      ];

      const matchingConfig = typeSenseConfigs.find(config => req.url.includes(config.url));
      if (matchingConfig) {
         // Keep public search requests free of user-specific headers so SSR transfer cache can replay them.
         header = new HttpHeaders({
            Accept: 'application/json',
            'X-TYPESENSE-API-KEY': matchingConfig.key,
         });
      }

      // if(!req.url.includes(environment.firebaseCloudFunctionsUrl+'/userRegistrationWithEmailSubmitUserData')) {
      //    header = header.append('Accept', 'application/json');
      // }

      // console.log(JSON.stringify(this.cookie.getAll()))
      //
      //  header = header.append('Cookie', JSON.stringify(this.cookie.getAll()));

      // if (req.url.includes(environment.typeSense.url)) {
      //    header = header.append(
      //       'X-TYPESENSE-API-KEY',
      //       environment.typeSense.searchApiKey
      //    );
      // }
      // else if (req.url.includes(environment.uatTypeSense.url)) {
      //    header = header.append(
      //       'X-TYPESENSE-API-KEY',
      //       'bgUgo63f47A7TriwuS3OMgENTaJcv8SF'
      //    );
      // }
      else if (!isNewFinancialApiRequest && token && !req.url.includes('/api/v1/auth/login')) {
         if (this.isBrowser) {
            token = localStorage.getItem('token') || '';
         }
         header = header.set('Authorization', 'Bearer ' + token);
      }

      if (req.url.includes(environment.revenueCat.v1.url)) {
         header = header.set('Authorization', 'Bearer ' + environment.revenueCat.v1.apiKey);
      }

      if(req.url.includes('user/create-ticket')) {
         header = header.delete('content-type');
         // header = header.set('process-data', 'false');
         // header = header.set('Content-Type', 'multipart/form-data');
         // contentType: false, //this is requireded please see answers above
            // processData: false, //this is requireded please see answers above
      }

      if(req.url.includes('portfolio-data/import-file-wrapper')) {
         header = header.delete('content-type');
         header = header.set('UPLOAD-TOKEN', `${environment.serviceCallToken}`);
      }

      if (this.isMultipartRequest(req)) {
         header = header.delete('content-type');
      }

      let authReq = req.clone({
         headers: header,
      });

      if (this.shouldUseServerGetCache(authReq)) {
         const cachedResponse = this.getCachedServerResponse(authReq);
         if (cachedResponse) {
            return of(cachedResponse);
         }
      }

      return next.handle(authReq).pipe(
         tap((event) => {
            if (
               this.shouldUseServerGetCache(authReq) &&
               event instanceof HttpResponse &&
               event.status === 200
            ) {
               this.cacheServerResponse(authReq, event);
            }
         }),
         catchError((err: any) => {
            if(
               err.status !== 400
            ) {
               this.loader.reset();
            }
            if (
               err.status === 401
            ) {
               this.authService.logout(false);
               this.clearSubscriptionInfo();
               if (this.isBrowser) {
                  window.location.href="/"
               }
            }

            if(err.status === 403) {
               this.handleForbiddenError(err);
            }

            // if (
            //    err.status === 402 ||
            //    (err.status === 0 && !req.url.includes('/api/v1/auth/login'))
            // ) {
            //    this.stripeFree.show(402);
            //    this.router.navigate(['/cabinet/']);
            // }
            // else if (
            //    err.status === 410 ||
            //    (err.status === 0 && !req.url.includes('/api/v1/auth/login'))
            // ) {
            //    this.stripeFree.show(410);

            // }
            // else if (
            //    err.status === 406 ||
            //    (err.status === 0 && !req.url.includes('/api/v1/auth/login'))
            // ) {
            //    this.stripeFree.show(406);

            // }
            // else if (err.status === 500 && !(req.url.includes('login') || req.url.includes('news'))) {

            //    this.router.navigate(['/pages/error-500']);

            // }
            // else if (
            //    err.status === 400 ||
            //    (err.status === 0 && !req.url.includes('/api/v1/auth/login'))
            // ) {
            // }
            // else {
            //    if (
            //       err &&
            //       err.error &&
            //       err.error.errors &&
            //       err.error.errors.length > 0
            //    ) {
            //       this.toaster.error(err.error.errors[0].message);
            //    } else {

            //    }
            // }
            return observableThrowError(() => err);
         })
      );
   }
}
